import { Component, defineComponent } from '@theme/component-base';
import { ThemeBus } from '@theme/events-bus';
import { EVENTS } from '@theme/event-constants';

const RECIPIENT_FLAG = 'properties[__shopify_send_gift_card_to_recipient]';

const DELIVERY_MODE = {
  SELF: 'self',
  RECIPIENT: 'recipient_form',
};

const FIELD_ERRORS = {
  email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
  name: { inputRef: 'recipientName', errorRef: 'nameError' },
  message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
  send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
};

/**
 * @param {Date} date
 * @returns {string}
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * @extends {Component}
 */
class GiftCardRecipientForm extends Component {
  /** @type {string} */
  #currentMode = DELIVERY_MODE.SELF;

  /** @type {{ fields_visible?: string, fields_hidden?: string, error?: string }} */
  #translations = {};

  requiredRefs = ['myEmailButton', 'recipientEmailButton', 'recipientFields', 'recipientEmail'];

  get #inputFields() {
    return [this.refs.recipientEmail, this.refs.recipientName, this.refs.recipientMessage, this.refs.recipientSendOn].filter(
      (field) => field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.#translations = this.#parseTranslations();
    this.validateRefs();

    this.#currentMode = this.#getDefaultDeliveryMode();
    this.#updateFormState();

    this.refs.myEmailButton.addEventListener('click', this.#handleDeliveryModeClick);
    this.refs.recipientEmailButton.addEventListener('click', this.#handleDeliveryModeClick);
    this.refs.recipientMessage?.addEventListener('input', this.#updateCharacterCount);

    ThemeBus.addEventListener(EVENTS.cartError, this.#handleCartError);
    ThemeBus.addEventListener(EVENTS.cartUpdated, this.#clearErrorMessages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.myEmailButton?.removeEventListener('click', this.#handleDeliveryModeClick);
    this.refs.recipientEmailButton?.removeEventListener('click', this.#handleDeliveryModeClick);
    this.refs.recipientMessage?.removeEventListener('input', this.#updateCharacterCount);

    ThemeBus.removeEventListener(EVENTS.cartError, this.#handleCartError);
    ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#clearErrorMessages);
  }

  #handleDeliveryModeClick = (event) => {
    const mode = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget.value : '';
    if (mode) this.#setDeliveryMode(mode);
  };

  #handleCartError = (event) => {
    const { message, errors, description } = event.detail?.data ?? {};
    if (!message && !errors) return;

    if (errors && typeof errors === 'object') {
      this.#showErrors(message || this.#translations.error || 'There was an error', errors);
    } else if (message) {
      this.#showErrors(message, description);
    }
  };

  #parseTranslations() {
    try {
      return JSON.parse(this.dataset.translations || '{}');
    } catch {
      return {};
    }
  }

  #getDefaultDeliveryMode() {
    const mode = this.dataset.defaultDelivery;
    return mode && Object.values(DELIVERY_MODE).includes(mode) ? mode : DELIVERY_MODE.SELF;
  }

  #getMaxDaysAhead() {
    const value = Number.parseInt(this.dataset.maxDaysAhead || '90', 10);
    return Number.isFinite(value) && value > 0 ? value : 90;
  }

  /**
   * @param {string} mode
   */
  #setDeliveryMode(mode) {
    if (!Object.values(DELIVERY_MODE).includes(mode) || this.#currentMode === mode) return;
    this.#currentMode = mode;
    this.#updateFormState();
  }

  #updateFormState() {
    const isRecipientMode = this.#currentMode === DELIVERY_MODE.RECIPIENT;

    this.#setButtonSelected(this.refs.myEmailButton, !isRecipientMode);
    this.#setButtonSelected(this.refs.recipientEmailButton, isRecipientMode);
    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#activateRecipientFields();
      this.#announce(this.#translations.fields_visible || 'Recipient form fields are now visible');
      this.refs.recipientEmail.focus();
    } else {
      this.#resetRecipientFields();
      this.#announce(this.#translations.fields_hidden || 'Recipient form fields are now hidden');
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: { mode: this.#currentMode, recipientFormVisible: isRecipientMode },
        bubbles: true,
      })
    );
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} selected
   */
  #setButtonSelected(button, selected) {
    if (selected) {
      button.setAttribute('data-selected', 'true');
    } else {
      button.removeAttribute('data-selected');
    }
    button.setAttribute('aria-pressed', String(selected));
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = message;
    }
  }

  #resetRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    this.querySelector(`input[name="${RECIPIENT_FLAG}"]`)?.remove();

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
  }

  #activateRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      field.removeAttribute('required');
    }

    this.refs.recipientEmail.setAttribute('required', '');

    if (this.dataset.requireName === 'true' && this.refs.recipientName) {
      this.refs.recipientName.setAttribute('required', '');
    }

    if (this.dataset.requireMessage === 'true' && this.refs.recipientMessage) {
      this.refs.recipientMessage.setAttribute('required', '');
    }

    if (!this.querySelector(`input[name="${RECIPIENT_FLAG}"]`)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = RECIPIENT_FLAG;
      input.value = 'on';
      this.appendChild(input);
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = String(new Date().getTimezoneOffset());
    }

    if (this.refs.recipientSendOn) {
      const today = new Date();
      const maxDate = new Date();
      maxDate.setDate(today.getDate() + this.#getMaxDaysAhead());

      this.refs.recipientSendOn.setAttribute('min', formatDate(today));
      this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));
    }

    this.#updateCharacterCount();
  }

  #updateCharacterCount = () => {
    const { characterCount, recipientMessage } = this.refs;
    if (!characterCount || !recipientMessage) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    characterCount.textContent = template
      .replace('[current]', String(recipientMessage.value.length))
      .replace('[max]', String(recipientMessage.maxLength));
  };

  /**
   * @param {string} title
   * @param {unknown} body
   */
  #showErrors(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      for (const [field, errorMessages] of Object.entries(body)) {
        const { inputRef, errorRef } = FIELD_ERRORS[field] ?? {};
        if (!inputRef) continue;

        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer instanceof HTMLElement) {
          const errorText = errorContainer.querySelector('span');
          if (errorText) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : String(errorMessages);
            errorText.textContent = `${message}.`;
          }
          errorContainer.classList.remove('hidden');
        }

        if (inputElement instanceof HTMLElement) {
          inputElement.setAttribute('aria-invalid', 'true');
          inputElement.setAttribute('aria-describedby', `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`);
        }
      }
    }

    this.#announce(title || this.#translations.error || 'There was an error with the form submission');
  }

  #clearErrorMessages = () => {
    for (const { errorRef } of Object.values(FIELD_ERRORS)) {
      const errorContainer = this.refs[errorRef];
      if (!(errorContainer instanceof HTMLElement)) continue;

      errorContainer.classList.add('hidden');
      const errorText = errorContainer.querySelector('span');
      if (errorText) errorText.textContent = '';
    }

    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  };
}

defineComponent('gift-card-recipient-form', GiftCardRecipientForm);
